'use client'
import { useEffect, useState, Fragment } from 'react'
import {Schedule, ScheduleType, ScheduleTypeOptions} from "models/model"
import { VehicleGetPaging } from "services/vehicleService"
import { TeacherGetPaging } from "services/teacherService"
import { ScheduleCreate, ScheduleUpdate, ScheduleDelete, ScheduleCreateRepeat } from "services/scheduleService"
import { Dialog, Transition } from '@headlessui/react'
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import ErrorModal from "popup/ErrorModal"
import ConfirmDialog from "popup/ConfirmDialog"
import { toast } from "react-hot-toast"

// Custom styles for DatePicker
const datePickerStyles = `
  .custom-datepicker .react-datepicker {
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
  .custom-datepicker .react-datepicker__header {
    background-color: white;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 0.5rem 0.5rem 0 0;
    padding-top: 0.5rem;
  }
  .custom-datepicker .react-datepicker__day-name {
    color: #6b7280;
    font-weight: 500;
    width: 2.5rem;
    line-height: 2rem;
  }
  .custom-datepicker .react-datepicker__day {
    width: 2.5rem;
    line-height: 2.5rem;
    color: #374151;
    border-radius: 0.375rem;
    transition: all 0.2s;
  }
  .custom-datepicker .react-datepicker__day:hover {
    background-color: #f3f4f6;
    color: #1f2937;
  }
  .custom-datepicker .react-datepicker__day--selected {
    background-color: #3b82f6;
    color: white;
    font-weight: 600;
  }
  .custom-datepicker .react-datepicker__day--selected:hover {
    background-color: #2563eb;
  }
  .custom-datepicker .react-datepicker__day--keyboard-selected {
    background-color: #dbeafe;
    color: #1e40af;
  }
  .custom-datepicker .react-datepicker__day--today {
    font-weight: 600;
    color: #3b82f6;
    background-color: #eff6ff;
  }
  .custom-datepicker .react-datepicker__day--outside-month {
    color: #9ca3af;
  }
  .custom-datepicker .react-datepicker__day--disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }
  .custom-datepicker .react-datepicker__day--disabled:hover {
    background-color: transparent;
  }
`

interface ScheduleModalProps {
    isOpen: boolean
    onClose: () => void
    courseId: string
    schedule?: Schedule | null
    refreshData: () => void
}

export default function ScheduleModal({ isOpen, onClose, courseId, schedule, refreshData }: ScheduleModalProps) {
    const [vehicles, setVehicles] = useState<any[]>([])
    const [teachers, setTeachers] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isRepeat, setIsRepeat] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [formData, setFormData] = useState({
        vehicleId: '',
        teacherId: '',
        startDate: new Date(),
        endDate: new Date(),
        type: ScheduleType.LyThuyet,
        notes: ''
    })

    const [errorModalOpen, setErrorModalOpen] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        if (isOpen) {
            fetchVehicles()
            fetchTeachers()
            if (schedule) {
                setFormData({
                    vehicleId: schedule.vehicleId || '',
                    teacherId: schedule.teacherId || '',
                    startDate: new Date(schedule.startDate),
                    endDate: new Date(schedule.endDate),
                    type: schedule.type || ScheduleType.LyThuyet,
                    notes: schedule.notes || ''
                })
                setIsRepeat(false)
            } else {
                setFormData({
                    vehicleId: '',
                    teacherId: '',
                    startDate: new Date(),
                    endDate: new Date(),
                    type: ScheduleType.LyThuyet,
                    notes: ''
                })
            }
        }
    }, [isOpen, schedule])

    const fetchVehicles = async () => {
        try {
            const res = await VehicleGetPaging({ courseId })
            setVehicles(res.result.items)
        } catch (error) {
            console.error('Failed to fetch vehicles:', error)
        }
    }

    const fetchTeachers = async () => {
        try {
            const res = await TeacherGetPaging({ courseId })
            setTeachers(res.result.items)
        } catch (error) {
            console.error('Failed to fetch teachers:', error)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleDateChange = (date: Date, type: string =  'start') => {
        if (type === 'end') {
            setFormData(prev => ({ ...prev, endDate: date }))
        }else{
            setFormData(prev => ({ ...prev, startDate: date }))
        }
    }

    const handleDelete = async () => {
        if (!schedule) return
        setShowDeleteConfirm(true)
    }

    const confirmDelete = async () => {
        setIsLoading(true)
        try {
            await ScheduleDelete(schedule?.id || '')
            toast.success('Xóa lịch học thành công')
            refreshData()
            onClose()
        } catch (error) {
            console.error('Failed to delete schedule:', error)
            setErrorMessage('Xóa lịch học thất bại')
            setErrorModalOpen(true)
        } finally {
            setIsLoading(false)
            setShowDeleteConfirm(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        try {
            const adjustTimezone = (date: Date) => {
                const newDate = new Date(date)
                newDate.setMinutes(newDate.getMinutes() - newDate.getTimezoneOffset())
                return newDate
            }

            // Set start date to beginning of day
            const startDate = new Date(formData.startDate)
            startDate.setHours(0, 0, 1, 0)

            // Set end date to end of day
            const endDate = new Date(formData.endDate)
            endDate.setHours(23, 59, 59, 999)

            const payload = {
                ...formData,
                ...(schedule && { id: schedule.id }),
                courseId,
                startDate: adjustTimezone(startDate).toISOString(),
                endDate: adjustTimezone(endDate).toISOString(),
                teacherId: formData.teacherId || null,
                vehicleId: formData.vehicleId || null,
                notes: formData.notes || null,
                type: parseInt(formData.type.toString(), 10)
            };

            const apiCall = schedule
                ? await ScheduleUpdate(payload)
                : isRepeat
                    ? await ScheduleCreateRepeat(payload)
                    : await ScheduleCreate(payload);

            const response = apiCall;


            toast.success(schedule ? 'Cập nhật lịch học thành công' : 'Thêm lịch học thành công');

            refreshData();
            onClose();
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || 'Đã xảy ra lỗi không xác định';
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <style>{datePickerStyles}</style>
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-6 text-gray-900"
                                    >
                                        {schedule ? 'Cập nhật lịch học' : 'Thêm lịch học mới'}
                                    </Dialog.Title>

                                    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Loại *</label>
                                            <select
                                                name="type"
                                                value={formData.type}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                                disabled={isLoading}
                                            >
                                                {ScheduleTypeOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Giáo viên</label>
                                            <select
                                                name="teacherId"
                                                value={formData.teacherId}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                disabled={isLoading}
                                            >
                                                <option value="">Chọn giáo viên</option>
                                                {teachers.map(teacher => (
                                                    <option key={teacher.id} value={teacher.id}>
                                                        {teacher.fullName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Phương tiện</label>
                                            <select
                                                name="vehicleId"
                                                value={formData.vehicleId}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                disabled={isLoading}
                                            >
                                                <option value="">Không chọn</option>
                                                {vehicles.map(vehicle => (
                                                    <option key={vehicle.id} value={vehicle.id}>
                                                        {vehicle.plateNumber}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Ngày bắt đầu *</label>
                                            <DatePicker
                                                selected={formData.startDate}
                                                // @ts-ignore
                                                onChange={event => handleDateChange(event, 'start')}
                                                dateFormat="dd/MM/yyyy"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                wrapperClassName="custom-datepicker w-full"
                                                calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                                required
                                                calendarStartDay={1}
                                                disabled={isLoading}
                                                showPopperArrow={false}
                                                renderCustomHeader={({
                                                    date,
                                                    decreaseMonth,
                                                    increaseMonth,
                                                    prevMonthButtonDisabled,
                                                    nextMonthButtonDisabled,
                                                }) => (
                                                    <div className="flex items-center justify-between px-2 py-2">
                                                        <button
                                                            onClick={decreaseMonth}
                                                            disabled={prevMonthButtonDisabled}
                                                            type="button"
                                                            className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                            aria-label="Tháng trước"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-5 w-5 text-gray-600"
                                                                viewBox="0 0 20 20"
                                                                fill="currentColor"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </button>

                                                        <span className="text-sm font-semibold text-gray-800">
                                                            {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                        </span>

                                                        <button
                                                            onClick={increaseMonth}
                                                            disabled={nextMonthButtonDisabled}
                                                            type="button"
                                                            className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                            aria-label="Tháng sau"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-5 w-5 text-gray-600"
                                                                viewBox="0 0 20 20"
                                                                fill="currentColor"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Ngày kết thúc *</label>
                                            <DatePicker
                                                selected={formData.endDate}
                                                // @ts-ignore
                                                onChange={event => handleDateChange(event, 'end')}
                                                dateFormat="dd/MM/yyyy"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                wrapperClassName="custom-datepicker w-full"
                                                calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                                required
                                                calendarStartDay={1}
                                                disabled={isLoading}
                                                showPopperArrow={false}
                                                renderCustomHeader={({
                                                    date,
                                                    decreaseMonth,
                                                    increaseMonth,
                                                    prevMonthButtonDisabled,
                                                    nextMonthButtonDisabled,
                                                }) => (
                                                    <div className="flex items-center justify-between px-2 py-2">
                                                        <button
                                                            onClick={decreaseMonth}
                                                            disabled={prevMonthButtonDisabled}
                                                            type="button"
                                                            className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                            aria-label="Tháng trước"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-5 w-5 text-gray-600"
                                                                viewBox="0 0 20 20"
                                                                fill="currentColor"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </button>

                                                        <span className="text-sm font-semibold text-gray-800">
                                                            {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                        </span>

                                                        <button
                                                            onClick={increaseMonth}
                                                            disabled={nextMonthButtonDisabled}
                                                            type="button"
                                                            className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                            aria-label="Tháng sau"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-5 w-5 text-gray-600"
                                                                viewBox="0 0 20 20"
                                                                fill="currentColor"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú</label>
                                            <textarea
                                                name="notes"
                                                value={formData.notes}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                rows={3}
                                                disabled={isLoading}
                                            />
                                        </div>

                                        {!schedule && (
                                            <div className="flex items-center">
                                                <input
                                                    id="repeat-checkbox"
                                                    type="checkbox"
                                                    checked={isRepeat}
                                                    onChange={(e) => setIsRepeat(e.target.checked)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    disabled={isLoading}
                                                />
                                                <label htmlFor="repeat-checkbox" className="ml-2 text-sm text-gray-700">
                                                    Lặp lại theo tuần
                                                </label>
                                            </div>
                                        )}

                                        <div className="flex justify-between pt-4">
                                            {schedule && (
                                                <button
                                                    type="button"
                                                    onClick={handleDelete}
                                                    disabled={isLoading}
                                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Xóa
                                                </button>
                                            )}
                                            <div></div>
                                            <div className="flex justify-end space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={onClose}
                                                    disabled={isLoading}
                                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                                                >
                                                    Hủy
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={isLoading}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isLoading ? (
                                                        <span className="flex items-center">
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            {schedule ? 'Đang cập nhật...' : 'Đang thêm...'}
                                                        </span>
                                                    ) : schedule ? 'Cập nhật' : 'Thêm mới'}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="Xác nhận xóa lịch học"
                description="Bạn có chắc chắn muốn xóa lịch học này?"
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
            />

            <ErrorModal
                isOpen={errorModalOpen}
                message={errorMessage}
                onClose={() => setErrorModalOpen(false)}
            />
        </>
    )
}